import { useRef } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import {
	editorOfflineStoragePluginKey,
	type EditorOfflineStorageDisabledReason,
} from '../../pm-plugins/main.ts';
import type { StepsDatabase } from '../../stepsDatabase.ts';
import type { EditorOfflineStoragePlugin } from '../../editorOfflineStoragePluginType.ts';

/**
 * If the offline syncing is disabled due to the sync lock
 * check if we can re-enable it
 */
export const useSyncEnabled = ({
	api,
	stepsDatabase,
	disabledRef,
}: {
	api: ExtractInjectionAPI<EditorOfflineStoragePlugin> | undefined;
	stepsDatabase: StepsDatabase;
	disabledRef: React.MutableRefObject<EditorOfflineStorageDisabledReason | boolean>;
}): void => {
	const queryingSyncLock = useRef<boolean>(false);

	usePluginStateEffect(api, ['editorOfflineStorage', 'connectivity', 'collabEdit'], () => {
		// Check if we're safe to re-enable the lock
		if (disabledRef.current === 'unableToSync' && !queryingSyncLock.current) {
			queryingSyncLock.current = true;
			stepsDatabase
				.isLocked()
				.then((locked) => {
					// If we're not locked (and still unable to sync) we can re-enable
					if (!locked && disabledRef.current === 'unableToSync') {
						api?.core.actions.execute(({ tr }) =>
							tr.setMeta(editorOfflineStoragePluginKey, {
								disabled: false,
							}),
						);
					}
				})
				.catch(() => {})
				.finally(() => {
					queryingSyncLock.current = false;
				});
		}
	});
};
